import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CreditSelector = ({ selectedCredits, onCreditsChange, onNext, onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRegistry, setFilterRegistry] = useState('');
  const [filterMethodology, setFilterMethodology] = useState('');

  const availableCredits = [
    {
      id: 'VCS-001-2024',
      serialNumber: 'VCS-001-2024-BR-14532-14532-1',
      projectName: 'Amazon Forest Conservation Project',
      methodology: 'VM0015',
      registry: 'Verra',
      vintage: 2023,
      quantity: 1000,
      unitPrice: 12.50,
      ccpStatus: 'approved',
      article6Status: 'compliant',
      correspondingAdjustment: true,
      integrityScore: 95,
      projectType: 'REDD+',
      country: 'Brazil',
      developer: 'Forest Carbon Ltd',
      verificationDate: '2024-03-15',
      expiryDate: '2034-03-15'
    },
    {
      id: 'GS-002-2024',
      serialNumber: 'GS-002-2024-KE-7891-7891-1',
      projectName: 'Kenya Solar Cookstoves Program',
      methodology: 'GS-TPDDTEC',
      registry: 'Gold Standard',
      vintage: 2023,
      quantity: 500,
      unitPrice: 15.75,
      ccpStatus: 'approved',
      article6Status: 'compliant',
      correspondingAdjustment: false,
      integrityScore: 92,
      projectType: 'Energy Efficiency',
      country: 'Kenya',
      developer: 'Clean Energy Solutions',
      verificationDate: '2024-02-20',
      expiryDate: '2034-02-20'
    },
    {
      id: 'ACR-003-2024',
      serialNumber: 'ACR-003-2024-US-5643-5643-1',
      projectName: 'California Grassland Carbon Sequestration',
      methodology: 'ACR-MS-02',
      registry: 'ACR',
      vintage: 2023,
      quantity: 750,
      unitPrice: 18.25,
      ccpStatus: 'pending',
      article6Status: 'non-applicable',
      correspondingAdjustment: false,
      integrityScore: 88,
      projectType: 'Soil Carbon',
      country: 'United States',
      developer: 'AgriCarbon Inc',
      verificationDate: '2024-01-10',
      expiryDate: '2034-01-10'
    }
  ];

  const registryOptions = [
    { value: '', label: 'All Registries' },
    { value: 'Verra', label: 'Verra' },
    { value: 'Gold Standard', label: 'Gold Standard' },
    { value: 'ACR', label: 'American Carbon Registry' }
  ];

  const methodologyOptions = [
    { value: '', label: 'All Methodologies' },
    { value: 'VM0015', label: 'VM0015 - REDD+' },
    { value: 'GS-TPDDTEC', label: 'GS-TPDDTEC - Energy Efficiency' },
    { value: 'ACR-MS-02', label: 'ACR-MS-02 - Soil Carbon' }
  ];

  const filteredCredits = availableCredits?.filter(credit => {
    const matchesSearch = credit?.projectName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         credit?.serialNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRegistry = !filterRegistry || credit?.registry === filterRegistry;
    const matchesMethodology = !filterMethodology || credit?.methodology === filterMethodology;
    
    return matchesSearch && matchesRegistry && matchesMethodology;
  });

  const handleCreditToggle = (creditId) => {
    const isSelected = selectedCredits?.includes(creditId);
    if (isSelected) {
      onCreditsChange(selectedCredits?.filter(id => id !== creditId));
    } else {
      onCreditsChange([...selectedCredits, creditId]);
    }
  };

  const getStatusBadge = (status, type) => {
    const statusConfig = {
      approved: { color: 'bg-success text-success-foreground', icon: 'CheckCircle' },
      pending: { color: 'bg-warning text-warning-foreground', icon: 'Clock' },
      compliant: { color: 'bg-success text-success-foreground', icon: 'Shield' },
      'non-applicable': { color: 'bg-muted text-muted-foreground', icon: 'Minus' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        <span>{status?.replace('-', ' ')?.toUpperCase()}</span>
      </span>
    );
  };

  const selectedCreditsData = availableCredits?.filter(credit => selectedCredits?.includes(credit?.id));
  const totalQuantity = selectedCreditsData?.reduce((sum, credit) => sum + credit?.quantity, 0);
  const totalValue = selectedCreditsData?.reduce((sum, credit) => sum + (credit?.quantity * credit?.unitPrice), 0);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-card-foreground mb-2">Select Carbon Credits</h2>
        <p className="text-sm text-muted-foreground">
          Choose verified carbon credits from your portfolio. Only CCP-approved credits are eligible for VCMI claims after 2026.
        </p>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Input
          type="search"
          placeholder="Search projects or serial numbers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
          className="w-full"
        />
        <Select
          placeholder="Filter by registry"
          options={registryOptions}
          value={filterRegistry}
          onChange={setFilterRegistry}
        />
        <Select
          placeholder="Filter by methodology"
          options={methodologyOptions}
          value={filterMethodology}
          onChange={setFilterMethodology}
        />
      </div>
      {/* Credits List */}
      <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
        {filteredCredits?.map((credit) => (
          <div
            key={credit?.id}
            className={`p-4 border rounded-lg transition-smooth cursor-pointer hover:border-primary/50 ${
              selectedCredits?.includes(credit?.id)
                ? 'border-primary bg-primary/5' :'border-border'
            }`}
            onClick={() => handleCreditToggle(credit?.id)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start space-x-3">
                <div className={`w-5 h-5 border-2 rounded flex items-center justify-center mt-1 ${
                  selectedCredits?.includes(credit?.id)
                    ? 'border-primary bg-primary' :'border-muted-foreground'
                }`}>
                  {selectedCredits?.includes(credit?.id) && (
                    <Icon name="Check" size={12} className="text-primary-foreground" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-card-foreground mb-1">{credit?.projectName}</h3>
                  <p className="text-xs font-mono text-muted-foreground mb-2">{credit?.serialNumber}</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {getStatusBadge(credit?.ccpStatus, 'CCP')}
                    {getStatusBadge(credit?.article6Status, 'Article 6')}
                    {credit?.correspondingAdjustment && (
                      <span className="inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium bg-accent text-accent-foreground">
                        <Icon name="ArrowRightLeft" size={12} />
                        <span>CA APPLIED</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-card-foreground">{credit?.quantity?.toLocaleString()} tCO2e</div>
                <div className="text-xs text-muted-foreground">${credit?.unitPrice}/tCO2e</div>
                <div className="text-xs font-medium text-success">${(credit?.quantity * credit?.unitPrice)?.toLocaleString()}</div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div>
                <span className="text-muted-foreground">Registry:</span>
                <div className="font-medium">{credit?.registry}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Methodology:</span>
                <div className="font-medium">{credit?.methodology}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Vintage:</span>
                <div className="font-medium">{credit?.vintage}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Integrity Score:</span>
                <div className="font-medium text-success">{credit?.integrityScore}/100</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Selection Summary */}
      {selectedCredits?.length > 0 && (
        <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6">
          <h3 className="font-medium text-card-foreground mb-2">Selection Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Selected Credits:</span>
              <div className="font-medium">{selectedCredits?.length} projects</div>
            </div>
            <div>
              <span className="text-muted-foreground">Total Quantity:</span>
              <div className="font-medium">{totalQuantity?.toLocaleString()} tCO2e</div>
            </div>
            <div>
              <span className="text-muted-foreground">Total Value:</span>
              <div className="font-medium text-success">${totalValue?.toLocaleString()}</div>
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} iconName="ArrowLeft" iconPosition="left">
          Back to Claim Type
        </Button>
        <Button
          onClick={onNext}
          disabled={selectedCredits?.length === 0}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Continue to Evidence
        </Button>
      </div>
    </div>
  );
};

export default CreditSelector;